import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AlertBar from '@common/components/AlertBar';
import ActionButton from '@common/components/ActionButton';
import {
  CloseCircleIcon,
  StateIcon,
  SensorIcon,
  DisableIcon,
  EnableIcon,
} from '@common/components/icons';
import * as styles from './styles.module.css';

function SelectedItemsBar({
  numberOfSelectedItems,
  itemLabel,
  onDeselectAll,
  actions = [],
}) {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleDropdownMouseEnter = (dropdownKey) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(dropdownKey);
  };

  const handleDropdownMouseLeave = () => {
    // Add a small delay before closing to allow moving to dropdown
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 250);
  };

  const handleDropdownAction = (actionKey, enabled) => {
    const action = actions.find((a) => a.key === actionKey);
    if (action?.onStateChange) {
      action.onStateChange(enabled);
    }
    setOpenDropdown(null);
  };

  const renderDropdownAction = (action, icon) => (
    <div
      key={action.key}
      className={styles['state-dropdown-container']}
      onMouseEnter={() => handleDropdownMouseEnter(action.key)}
      onMouseLeave={handleDropdownMouseLeave}
    >
      <ActionButton
        icon={icon}
        text={action.text}
        disabled={action.disabled}
        onClick={action.onClick}
      />
      {openDropdown === action.key && (
        <div className={styles['state-dropdown']}>
          <ActionButton
            icon={EnableIcon}
            text={t('common.enable')}
            onClick={() => handleDropdownAction(action.key, true)}
            className={styles['dropdown-action-button']}
          />
          <ActionButton
            icon={DisableIcon}
            text={t('common.disable')}
            onClick={() => handleDropdownAction(action.key, false)}
            className={styles['dropdown-action-button']}
          />
        </div>
      )}
    </div>
  );

  const renderAction = (action) => {
    if (action.key === 'state') {
      return renderDropdownAction(action, StateIcon);
    }

    if (action.key === 'passiveSensors') {
      return renderDropdownAction(action, SensorIcon);
    }

    return (
      <div key={action.key} className={styles['action-button-container']}>
        <ActionButton
          icon={action.icon}
          text={action.text}
          onClick={action.onClick}
          disabled={action.disabled}
          title={action.title}
          className={action.className}
          testId={action.testId}
        />
      </div>
    );
  };

  return (
    <AlertBar>
      <div className={styles['selected-items-bar']}>
        <div className={styles['left-section']}>
          <button
            type="button"
            onClick={onDeselectAll}
            className={styles['close-button']}
            aria-label={`Deselect all ${itemLabel.toLowerCase()}`}
          >
            <CloseCircleIcon size="16" />
          </button>
          <span className={styles['selected-count']}>
            {numberOfSelectedItems} {itemLabel} selected
          </span>
        </div>
        <div className={styles['right-section']}>
          {actions.map(renderAction)}
        </div>
      </div>
    </AlertBar>
  );
}

export default SelectedItemsBar;
